import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Sun, Moon, RefreshCw, Share2, Clock, Search, ChevronDown, PanelLeft, PanelLeftClose, Command, Menu, X, Pencil, Download, Loader2, MoreHorizontal, ClipboardPaste } from 'lucide-react'
import { useSync, useWorkspaceMetadata, useRegistry, useFileSystem } from '../hooks/useWorkspace'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { timeAgo } from '../utils/writerUtils'
import JSZip from 'jszip'
import EmojiPicker from './EmojiPicker'
import SyncDetailPopover from './SyncDetailPopover'
import { PresenceIndicator } from './WorkspaceSettings'
import { useToast } from './Toast'
import './TopBar.css'

interface TopBarProps {
    theme: 'light' | 'dark'
    onToggleTheme: () => void
    onOpenQuickOpen: () => void
    onOpenSearch: () => void
    onOpenShare: () => void
    onOpenHistory: () => void
    onOpenPicker: () => void
    onOpenProfile: () => void
    onToggleSidebar: () => void
    onPaste?: () => void
    sidebarCollapsed: boolean
    onToggleDrawer: () => void
    drawerOpen: boolean
    avatarEmoji?: string
    isGuest?: boolean
    onOpenSettings?: () => void
    onOpenPath?: (path: string) => void
}

export default function TopBar({ theme, onToggleTheme, onOpenQuickOpen, onOpenSearch, onOpenShare, onOpenHistory, onOpenPicker, onOpenProfile, onToggleSidebar, sidebarCollapsed, onToggleDrawer, drawerOpen, avatarEmoji = '🦊', isGuest = false, onOpenSettings, onPaste, onOpenPath }: TopBarProps) {
    const { syncing, lastSyncAt, pendingChanges, syncNow, lastSyncError, connectivity, synced, syncLog, conflictPaths, syncProgress, syncPolicy, syncPaused, pauseSync, resumeSync } = useSync()
    const { emoji: workspaceEmoji, label: workspaceLabel, blobId, canWrite } = useWorkspaceMetadata()
    const { updateEntry } = useRegistry()
    const online = useOnlineStatus()
    const effectiveConnectivity = online ? connectivity : 'offline'
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [editingWs, setEditingWs] = useState(false)
    const [draftLabel, setDraftLabel] = useState(workspaceLabel)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const editInputRef = useRef<HTMLInputElement>(null)
    const editAreaRef = useRef<HTMLDivElement>(null)
    const suppressBlurRef = useRef(false)
    const { files, readFile } = useFileSystem()
    const [zipping, setZipping] = useState(false)
    const [overflowOpen, setOverflowOpen] = useState(false)
    const moreBtnRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
    const toast = useToast()

    useLayoutEffect(() => {
        if (!overflowOpen || !moreBtnRef.current) return
        const updatePosition = () => {
            const rect = moreBtnRef.current?.getBoundingClientRect()
            if (!rect) return
            setMenuStyle({
                top: rect.bottom + 6,
                right: window.innerWidth - rect.right,
                position: 'fixed',
            })
        }
        updatePosition()
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)

        if (overflowOpen) {
            requestAnimationFrame(() => {
                const firstBtn = menuRef.current?.querySelector('button')
                if (firstBtn) (firstBtn as HTMLElement).focus()
            })
        } else {
            moreBtnRef.current?.focus()
        }

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [overflowOpen])

    useEffect(() => {
        if (!overflowOpen) return
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setOverflowOpen(false); e.stopPropagation() }
        }
        window.addEventListener('keydown', handleKey, true)
        return () => window.removeEventListener('keydown', handleKey, true)
    }, [overflowOpen])

    const handleDownloadZip = useCallback(async () => {
        if (zipping) return
        if (!files.length) {
            toast('Workspace is empty — nothing to download', { type: 'info' })
            return
        }
        setZipping(true)
        try {
            const zip = new JSZip()
            let count = 0
            for (const f of files) {
                try {
                    const text = await readFile(f.path)
                    if (text !== null) {
                        zip.file(f.path.replace(/^\//, ''), text)
                        count++
                    }
                } catch {
                    continue
                }
            }
            const blob = await zip.generateAsync({ type: 'blob' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${(workspaceLabel || 'workspace').replace(/[^a-z0-9-_]/gi, '_')}.zip`
            a.click()
            URL.revokeObjectURL(url)
            toast(`📦 Downloaded ${count} files`, { type: 'success', duration: 3000 })
        } catch {
            toast('Failed to create ZIP', { type: 'error' })
        } finally {
            setZipping(false)
        }
    }, [zipping, files, readFile, workspaceLabel, toast])

    useEffect(() => { setDraftLabel(workspaceLabel) }, [workspaceLabel])
    useEffect(() => {
        if (editingWs && !showEmojiPicker && editInputRef.current) {
            editInputRef.current.focus()
            editInputRef.current.select()
        }
    }, [editingWs, showEmojiPicker])

    const commitEdit = useCallback(() => {
        const trimmed = draftLabel.trim()
        if (trimmed && trimmed !== workspaceLabel) {
            updateEntry(blobId, { label: trimmed })
        }
        setEditingWs(false)
        setShowEmojiPicker(false)
    }, [draftLabel, workspaceLabel, blobId, updateEntry])

    const handleInputBlur = useCallback(() => {
        if (suppressBlurRef.current) {
            suppressBlurRef.current = false
            return
        }
        setTimeout(() => {
            if (editAreaRef.current?.contains(document.activeElement)) return
            commitEdit()
        }, 0)
    }, [commitEdit])

    return (
        <header className="topbar">
            <div className="topbar-left">
                <button
                    className="btn btn-icon mobile-drawer-toggle"
                    onClick={onToggleDrawer}
                    aria-label={drawerOpen ? 'Close files' : 'Show files'}
                >
                    {drawerOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
                <button
                    className="btn btn-icon sidebar-topbar-toggle"
                    onClick={onToggleSidebar}
                    title={sidebarCollapsed ? 'Show files' : 'Hide files'}
                >
                    {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
                </button>

                {editingWs ? (
                    <div className="topbar-ws-edit" ref={editAreaRef}>
                        <span
                            className="topbar-ws-emoji topbar-ws-emoji-editable"
                            onMouseDown={() => { suppressBlurRef.current = true }}
                            onClick={() => setShowEmojiPicker(v => !v)}
                            role="button"
                            title="Change icon"
                        >
                            {workspaceEmoji}
                        </span>
                        <input
                            ref={editInputRef}
                            className="topbar-ws-edit-input"
                            style={showEmojiPicker ? { display: 'none' } : undefined}
                            value={draftLabel}
                            onChange={e => setDraftLabel(e.target.value)}
                            onBlur={handleInputBlur}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
                                if (e.key === 'Escape') { setEditingWs(false); setShowEmojiPicker(false); setDraftLabel(workspaceLabel) }
                            }}
                        />
                    </div>
                ) : (
                    <div className="topbar-ws-group">
                        {canWrite ? (
                            <span
                                className="topbar-ws-emoji topbar-ws-emoji-editable"
                                onClick={() => setShowEmojiPicker(v => !v)}
                                role="button"
                                title="Change icon"
                                style={{ cursor: 'pointer' }}
                            >
                                {workspaceEmoji}
                            </span>
                        ) : (
                            <span className="topbar-ws-emoji">{workspaceEmoji}</span>
                        )}
                        <button className="topbar-workspace-btn" onClick={onOpenPicker} title="Switch workspace (Cmd+K)">
                            <span className="topbar-ws-meta">
                                <span className="topbar-ws-label">{workspaceLabel}</span>
                                <span className="topbar-ws-id">{blobId.slice(0, 8)}</span>
                            </span>
                            {!canWrite && <span className="topbar-ws-readonly">Read-only</span>}
                            <ChevronDown size={14} className="topbar-ws-chevron" />
                        </button>
                        {canWrite && (
                            <button
                                className="topbar-ws-edit-btn"
                                onClick={() => setEditingWs(true)}
                                title="Edit workspace name & icon"
                                aria-label="Edit workspace name and icon"
                            >
                                <Pencil size={13} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="topbar-center-group">
                <div className={`topbar-center ${syncing || lastSyncError || effectiveConnectivity !== 'connected' ? 'is-transient' : ''}`} onClick={() => setPopoverOpen(v => !v)} role="button" tabIndex={0} style={{ cursor: 'pointer', position: 'relative' }}>
                    <span
                        className={`topbar-status-dot ${effectiveConnectivity}${syncing ? ' syncing' : lastSyncError ? ' error' : pendingChanges > 0 ? ' pending' : ''}`}
                        title={effectiveConnectivity === 'unreachable' ? 'Server unreachable' : effectiveConnectivity === 'offline' ? 'No internet connection' : 'Connected'}
                    />
                    <span className="topbar-status-text">
                        {effectiveConnectivity === 'unreachable' ? 'Server unreachable' : effectiveConnectivity === 'offline' ? 'Offline' : lastSyncError ? 'Sync error' : syncPaused ? 'Sync paused' : syncing && syncProgress ? `Uploading ${syncProgress.objectsUploaded}/${syncProgress.objectsTotal}` : syncing ? 'Syncing…' : pendingChanges > 0 ? `${pendingChanges} pending` : syncPolicy === 'auto' ? `Auto-sync ${timeAgo(lastSyncAt, { maxDepth: 'seconds', suffix: ' ago' })}` : `On-demand ${timeAgo(lastSyncAt, { maxDepth: 'seconds', suffix: ' ago' })}`}
                    </span>
                    {popoverOpen && (
                        <SyncDetailPopover
                            syncing={syncing}
                            synced={synced}
                            lastSyncAt={lastSyncAt}
                            pendingChanges={pendingChanges}
                            connectivity={effectiveConnectivity}
                            lastSyncError={lastSyncError}
                            syncLog={syncLog}
                            conflictPaths={conflictPaths}
                            syncProgress={syncProgress}
                            syncPaused={syncPaused}
                            syncPolicy={syncPolicy}
                            syncNow={syncNow}
                            pauseSync={pauseSync}
                            resumeSync={resumeSync}
                            onOpenPath={onOpenPath}
                            onClose={() => setPopoverOpen(false)}
                        />
                    )}
                </div>
                <button className="btn btn-icon topbar-refresh-btn" onClick={syncNow} disabled={syncing} title="Sync now">
                    <RefreshCw size={16} className={syncing ? 'spin' : ''} />
                </button>
            </div>

            <div className="topbar-right">
                {canWrite && onPaste && (
                    <button className="btn btn-icon" onClick={onPaste} title="Klistra in">
                        <ClipboardPaste size={18} />
                    </button>
                )}
                <button className="btn btn-icon topbar-inline-action" onClick={handleDownloadZip}
                    title="Download workspace as ZIP" disabled={zipping}>
                    {zipping ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
                </button>
                <button className="btn btn-icon topbar-inline-action" onClick={onOpenSearch} title="Search (Cmd+Shift+F)">
                    <Search size={18} />
                </button>
                <button className="btn btn-icon topbar-inline-action" onClick={onOpenQuickOpen} title="Quick open (Cmd+P)">
                    <Command size={18} />
                </button>
                <button className="btn btn-icon topbar-inline-action" onClick={onOpenShare} title="Share">
                    <Share2 size={18} />
                </button>
                <button className="btn btn-icon topbar-inline-action" onClick={onOpenHistory} title="History">
                    <Clock size={18} />
                </button>
                <button className="btn btn-icon topbar-inline-action" onClick={onToggleTheme} title={theme === 'light' ? 'Dark mode' : 'Light mode'}>
                    {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                </button>

                <div style={{ position: 'relative' }}>
                    <button ref={moreBtnRef} className="btn btn-icon topbar-more-btn" onClick={() => setOverflowOpen(v => !v)}
                        title="More actions" aria-label="More actions">
                        <MoreHorizontal size={18} />
                    </button>
                    {overflowOpen && createPortal(
                        <>
                            <div className="topbar-overflow-backdrop" onClick={() => setOverflowOpen(false)} />
                            <div ref={menuRef} className="topbar-overflow-menu" style={menuStyle}>
                                <button className="topbar-overflow-item" onClick={() => { handleDownloadZip(); setOverflowOpen(false) }}
                                    disabled={zipping}>
                                    {zipping ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                                    <span>Download ZIP</span>
                                </button>
                                <button className="topbar-overflow-item" onClick={() => { onOpenShare(); setOverflowOpen(false) }}>
                                    <Share2 size={16} /><span>Share</span>
                                </button>
                                <button className="topbar-overflow-item" onClick={() => { onOpenHistory(); setOverflowOpen(false) }}>
                                    <Clock size={16} /><span>History</span>
                                </button>
                                <button className="topbar-overflow-item" onClick={() => { onOpenQuickOpen(); setOverflowOpen(false) }}>
                                    <Command size={16} /><span>Quick Open</span>
                                </button>
                                <button className="topbar-overflow-item" onClick={() => { onOpenSearch(); setOverflowOpen(false) }}>
                                    <Search size={16} /><span>Search</span>
                                </button>
                                <div className="topbar-overflow-sep" />
                                <button className="topbar-overflow-item" onClick={() => { onToggleTheme(); setOverflowOpen(false) }}>
                                    {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                                    <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
                                </button>
                            </div>
                        </>,
                        document.body
                    )}
                </div>

                <PresenceIndicator onClick={onOpenSettings} />
                <span className="topbar-divider" />
                <button className={`topbar-avatar-btn ${isGuest ? 'topbar-avatar-guest' : ''}`} onClick={onOpenProfile} title={isGuest ? 'Sign in' : 'Profile'}>
                    <span className="topbar-avatar-emoji">{avatarEmoji}</span>
                </button>
            </div>

            {showEmojiPicker && createPortal(
                <EmojiPicker
                    currentEmoji={workspaceEmoji}
                    onSelect={(emoji) => { updateEntry(blobId, { emoji }); setShowEmojiPicker(false) }}
                    onClose={() => { setShowEmojiPicker(false); if (editingWs) editInputRef.current?.focus() }}
                />,
                document.body
            )}
        </header>
    )
}
